import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, GraduationCap, ChevronRight, FileText, ClipboardCheck, FileStack, PlayCircle, Zap, MessageSquare, Sparkles } from 'lucide-react';
import { Card } from '../components/ui/card';
import { navodayaLevels, navodayaSubjects, navodayaFeatures, sampleNotes } from '../mock';
import { FeatureTile } from '../components/FeatureTile';

const subjectKeyOf = (s) => s.nameEn.toLowerCase().split(' ')[0];
const countNotes = (key) => (sampleNotes.navodaya || []).filter((n) => (n.subjectKey || '').toLowerCase() === key).length;

const iconMap = { FileText, ClipboardCheck, FileStack, PlayCircle, Zap, MessageSquare };

export default function NavodayaPage() {
  const [level, setLevel] = useState('jnvst-6');
  const subjects = navodayaSubjects[level] || [];
  const active = navodayaLevels.find((l) => l.id === level);
  return (
    <div className="px-4 pt-4 space-y-5">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 font-medium">
        <ArrowLeft size={16} /> ମୁଖ୍ୟ ପେଜ କୁ ଫେରନ୍ତୁ
      </Link>

      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl p-5 text-white shadow-lg"
           style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 55%, #e11d48 100%)' }}>
        <div className="absolute -top-8 -right-8 opacity-20"><GraduationCap size={110} /></div>
        <GraduationCap size={26} />
        <h1 className="text-xl font-extrabold mt-2">ନବୋଦୟ ପ୍ରବେଶିକା ପରୀକ୍ଷା</h1>
        <p className="text-xs text-white/90 font-medium">Navodaya Vidyalaya Entrance Exam (JNVST)</p>
        <p className="text-[11px] text-white/85 mt-1">Class 6 ଓ Class 9 (Lateral) — ସମ୍ପୂର୍ଣ୍ଣ ପ୍ରସ୍ତୁତି</p>
      </div>

      {/* Level Toggle */}
      <div className="grid grid-cols-2 gap-3">
        {navodayaLevels.map((l) => {
          const isActive = l.id === level;
          return (
            <button key={l.id} onClick={() => setLevel(l.id)}
              className={`rounded-2xl p-3 text-left border-2 transition-all ${isActive ? 'border-orange-500 bg-orange-50/60 shadow-sm' : 'border-gray-200 bg-white'}`}>
              <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${l.color} text-white flex items-center justify-center mb-2`}>
                <GraduationCap size={17} />
              </div>
              <div className="font-bold text-xs text-gray-900 leading-tight">{l.title}</div>
              <div className="text-[10px] text-gray-600 mt-0.5">{l.titleOdia}</div>
              <div className="text-[10px] text-gray-500 mt-1">{l.duration}</div>
            </button>
          );
        })}
      </div>

      {/* Subjects */}
      <div>
        <h3 className="text-base font-extrabold text-gray-900">ବିଷୟ · Subjects <span className="text-xs font-medium text-gray-500">({active?.title})</span></h3>
        <div className="space-y-2 mt-3">
          {subjects.map((s, i) => {
            const key = subjectKeyOf(s);
            const n = countNotes(key);
            return (
            <Link key={i} to={`/notes/navodaya?subject=${encodeURIComponent(key)}`} data-testid={`navodaya-subject-${key}`}
              className="rounded-2xl p-3 border border-gray-200 bg-white flex items-center gap-3 hover:border-orange-300 transition-colors">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-lg ${s.color}`}>
                {s.icon}
              </div>
              <div className="flex-1">
                <div className="font-bold text-sm text-gray-900">{s.name}</div>
                <div className="text-[11px] text-gray-500">{s.nameEn} · {n > 0 ? `${n} ଅଧ୍ୟାୟ ଉପଲବ୍ଧ` : 'ନୋଟ୍ସ ଶୀଘ୍ର ଆସୁଛି'}</div>
              </div>
              <ChevronRight size={18} className="text-gray-400" />
            </Link>
            );
          })}
        </div>
      </div>

      {/* Features */}
      <div>
        <h3 className="text-base font-extrabold text-gray-900 flex items-center gap-1.5">
          <Sparkles size={15} className="text-orange-500" /> ଅଧ୍ୟୟନ ସାମଗ୍ରୀ · Study Tools
        </h3>
        <div className="grid grid-cols-2 gap-3 mt-3">
          {navodayaFeatures.map((f, i) => (
            <FeatureTile key={i} item={f} icon={iconMap[f.icon] || FileText} gradient={f.gradient} testId={`navodaya-tile-${i}`} />
          ))}
        </div>
      </div>
    </div>
  );
}
